import * as React from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Autocomplete,
} from '@mui/material';
import {
  Response,
  SuccessResponse,
  Class,
  Coach,
} from '../../types/ClassTable.type';
import { apiGet, apiPost } from '../../utils/apiClient';
import { notifyAPIClient } from '../../utils/toast';

type AddCoachDialogProps = {
  open: boolean;
  handleClose: () => void;
  selectedClass: Class | null;
  fetchCoaches: () => void;
};

const AddCoachDialog = ({
  open,
  handleClose,
  selectedClass,
  fetchCoaches,
}: AddCoachDialogProps) => {
  const [coaches, setCoaches] = React.useState<Coach[]>([]);
  const [selectedCoach, setSelectedCoach] = React.useState<Coach | null>(null);

  const fetchAllCoaches = async () => {
    if (selectedClass) {
      try {
        const response = await apiGet<Response<Coach>>(
          `/class/coach/list-all?classId=${selectedClass.id}`,
        );
        setCoaches(response.data);
      } catch (error) {
        console.error('Error fetching coaches:', error);
      }
    }
  };

  const handleAddCoach = async () => {
    if (!selectedCoach) {
      notifyAPIClient('Please select a coach to add.');
      return;
    } else {
      try {
        const response = await apiPost<SuccessResponse>('/class/coach/add', {
          classId: selectedClass?.id,
          coachId: selectedCoach?.id,
        });

        notifyAPIClient(response.message);
        fetchCoaches();
        handleCloseDialog();
      } catch (error) {
        console.error('Error adding coach:', error);
        notifyAPIClient('An error occurred while adding the coach.');
      }
    }
  };

  const handleCloseDialog = () => {
    handleClose();
    setCoaches([]);
    setSelectedCoach(null);
  };

  React.useEffect(() => {
    if (open) {
      fetchAllCoaches();
    }
  }, [open]);

  return (
    <Dialog fullWidth open={open} onClose={handleCloseDialog}>
      <DialogTitle>Add New Coach</DialogTitle>
      <DialogContent>
        <Autocomplete
          options={coaches}
          getOptionLabel={(option) =>
            option?.id && option?.name && option?.email
              ? `${option.id} - ${option.name} (${option.email})`
              : ''
          }
          isOptionEqualToValue={(option, value) => option?.id === value?.id}
          filterOptions={(options, state) =>
            options.filter((opt) => {
              const id = opt.id?.toString().toLowerCase();
              const name = opt.name?.toLowerCase();
              const email = opt.email?.toLowerCase();
              const input = state.inputValue.toLowerCase();

              return (
                id.includes(input) ||
                name?.includes(input) ||
                email?.includes(input)
              );
            })
          }
          value={selectedCoach}
          onChange={(_, newValue) => newValue && setSelectedCoach(newValue)}
          renderInput={(params) => (
            <TextField
              {...params}
              className="required-field"
              label="Coach (ID - Name <Email>)"
              variant="standard"
              name="autocomplete"
            />
          )}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCloseDialog}>Cancel</Button>
        <Button onClick={handleAddCoach}>Add</Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddCoachDialog;
